import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { Button, Modal, PageHeader, Table, Tooltip } from "antd";
import moment from "moment-jalaali";

import { CheckPermission, GetUrlParams, ObjectToURL } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals, Permissions } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/RoleForm";

const Component = ({ onSetRoute, routeName, parentName, permissions }) => {
  const initialState = { data: [], pagination: {}, permissionList: [] };
  const tableColumns = [
    {
      title: Resources.row,
      dataIndex: "index",
      className: "center-cell",
      width: "60px",
      render: (value, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: Resources.title,
      dataIndex: "name",
    },
    {
      title: `${Resources.date} ${Resources.record}`,
      dataIndex: "createdAt",
      className: "center-cell desc-cell ltr-cell",
      width: "150px",
      render: (value) => {
        const [date, time] = moment(value)
          .format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
          .split(" ");

        return (
          <>
            <span className="name">{date}</span>
            <small className="desc">{time}</small>
          </>
        );
      },
    },
    {
      title: Resources.action,
      dataIndex: "id",
      className: "center-cell",
      width: "120px",
      render: (value, record) => {
        return record.isPermanent ? (
          <></>
        ) : (
          <>
            {permissions.update && (
              <Tooltip title={Resources.edit}>
                <Button
                  type="link"
                  icon={<i className="fa fa-pencil-alt" />}
                  onClick={() => handleForm(record)}
                />
              </Tooltip>
            )}
            {permissions.delete && (
              <Tooltip title={Resources.delete}>
                <Button
                  type="link"
                  icon={<i className="fa fa-trash-alt" />}
                  onClick={() => handleDelete(value)}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];
  const [state, setState] = useState(initialState);
  const { data, loading, pagination, permissionList, selected } = state;
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const fetchData = () => {
    const params = GetUrlParams(search);

    setState((prevState) => ({ ...prevState, loading: true }));

    API.role
      .list({ ...params, pageSize: process.env.REACT_APP_PAGE_SIZE })
      .then(({ data, meta: { currentPage, perPage, total } }) => {
        setState((prevState) => ({
          ...prevState,
          data,
          pagination: {
            current: currentPage,
            pageSize: perPage,
            total,
            showSizeChanger: false,
          },
          loading: false,
        }));
      })
      .catch(() => {
        setState((prevState) => ({ ...prevState, loading: false }));
      });
  };

  const handleForm = (selected, reload) => {
    if (reload) {
      fetchData();
    } else {
      setState((prevState) => ({ ...prevState, selected }));

      navigate(
        `${pathname}${search}#${selected ? Modals.EDIT : Modals.CREATE}`
      );
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: Resources.deleteConfirm,
      okText: Resources.yes,
      okType: "danger",
      cancelText: Resources.no,
      onOk: () => {
        setState((prevState) => ({ ...prevState, loading: true }));

        API.role
          .delete(id)
          .then(() => {
            setState((prevState) => ({ ...prevState, loading: false }));

            fetchData();
          })
          .catch(() => {
            setState((prevState) => ({ ...prevState, loading: false }));
          });
      },
    });
  };

  const handleTable = ({ current }) => {
    const params = GetUrlParams(search);
    const query = ObjectToURL({ ...params, page: current });

    navigate(`${pathname}?${query}`);
  };

  const componentDidMount = () => {
    onSetRoute({ openKey: parentName, selectedKey: routeName });

    API.permissions()
      .then((data) => {
        setState((prevState) => ({ ...prevState, permissionList: data }));
      })
      .catch(() => {});

    fetchData();
  };

  useEffect(componentDidMount, []);

  return (
    <>
      <PageHeader
        title={Resources.roles}
        extra={
          permissions.create && (
            <Button
              type="primary"
              onClick={() => handleForm()}
              loading={loading}
            >
              {Resources.add}
            </Button>
          )
        }
      />
      <Table
        columns={tableColumns}
        dataSource={data}
        loading={loading}
        onChange={handleTable}
        pagination={{ ...pagination, showSizeChanger: false }}
        scroll={{ x: 930 }}
        rowKey="id"
      />
      <FormModal
        data={selected}
        onUpdate={handleForm}
        options={{ permissions: permissionList }}
      />
    </>
  );
};

export default connect(
  (state) => {
    return {
      permissions: {
        create: CheckPermission(
          state.user.permissions,
          Permissions.ROLE_CREATE
        ),
        delete: CheckPermission(
          state.user.permissions,
          Permissions.ROLE_DELETE
        ),
        update: CheckPermission(
          state.user.permissions,
          Permissions.ROLE_UPDATE
        ),
      },
    };
  },
  (dispatch) => {
    return {
      onSetRoute: (data) => dispatch(setRoute(data)),
    };
  }
)(Component);
